import { ArrowLeft, ArrowRight, Heart, Mail } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import AppointmentPicker from "./AppointmentPicker";
import ShareActions from "./ShareActions";

const NOT_SPECIFIED = "Nicht angegeben";

function specSlug(label) {
  return label.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss").replace(/[^a-z0-9]+/g, "-");
}

function formatDoors(value) {
  return value && value !== NOT_SPECIFIED ? `${value} Türen` : NOT_SPECIFIED;
}

function formatSeats(value) {
  return value && value !== NOT_SPECIFIED ? `${value} Sitze` : NOT_SPECIFIED;
}

function buildSpecGroups(vehicle) {
  return [
    {
      title: "Basisdaten",
      rows: [
        ["Fahrzeugkategorie", vehicle.category],
        ["Erstzulassung", vehicle.first_registration],
        ["Laufleistung", vehicle.mileage],
        ["Vorbesitzer", vehicle.previous_owners],
        ["Zustand", vehicle.condition],
        ["Fahrbereit", vehicle.ready_to_drive === true ? "Ja" : vehicle.ready_to_drive === false ? "Nein" : NOT_SPECIFIED]
      ]
    },
    {
      title: "Motor & Antrieb",
      rows: [
        ["Leistung", vehicle.power],
        ["Kraftstoff", vehicle.fuel],
        ["Getriebe", vehicle.gearbox],
        ["Hubraum", vehicle.displacement],
        ["Schadstoffklasse", vehicle.emission_class]
      ]
    },
    {
      title: "Karosserie",
      rows: [
        ["Außenfarbe", vehicle.color],
        ["Türen", formatDoors(vehicle.doors)],
        ["Sitzplätze", formatSeats(vehicle.seats)]
      ]
    }
  ];
}

export default function VehicleDetailPage({ vehicle, onBack, onPrepareInquiry, isFavorite, onToggleFavorite }) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const pointerStart = useRef(null);
  const images = vehicle?.images?.length ? vehicle.images : [vehicle?.image];
  const showImage = (index) => setActiveImageIndex((index + images.length) % images.length);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [vehicle?.id]);

  useEffect(() => {
    if (images.length < 2) return undefined;
    const handleKeyDown = (event) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      if (event.key === "ArrowRight") setActiveImageIndex((index) => (index + 1) % images.length);
      if (event.key === "ArrowLeft") setActiveImageIndex((index) => (index - 1 + images.length) % images.length);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [images.length]);

  const handlePointerDown = (event) => {
    if (images.length < 2 || event.pointerType === "mouse") return;
    pointerStart.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handlePointerUp = (event) => {
    if (pointerStart.current === null) return;
    const distance = event.clientX - pointerStart.current;
    if (Math.abs(distance) > 28) showImage(activeImageIndex + (distance < 0 ? 1 : -1));
    pointerStart.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  if (!vehicle) return <section className="vehicle-detail-page page-shell" data-testid="vehicle-detail-loading"><button className="detail-back-button" type="button" onClick={onBack} data-testid="vehicle-detail-back-button"><ArrowLeft size={16} /> Zurück zum Bestand</button><p className="vehicle-detail-missing">Fahrzeug wird geladen oder ist nicht mehr im Bestand …</p></section>;

  const specGroups = buildSpecGroups(vehicle);

  return <section className="vehicle-detail-page page-shell" aria-labelledby="vehicle-detail-title" data-testid="vehicle-detail-page">
    <button className="detail-back-button" type="button" onClick={onBack} data-testid="vehicle-detail-back-button"><ArrowLeft size={16} /> Zurück zum Bestand</button>
    <div className="vehicle-detail-layout">
      <div className="vehicle-detail-gallery">
        <div className="vehicle-detail-image" onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerCancel={() => { pointerStart.current = null; }}>
          <img src={images[activeImageIndex]} alt={`${vehicle.brand} ${vehicle.title}, Ansicht ${activeImageIndex + 1} von ${images.length}`} />
          <span className="vehicle-badge detail-badge">{vehicle.badge}</span>
          <button className={`vehicle-save detail-save ${isFavorite ? "is-saved" : ""}`} type="button" aria-pressed={isFavorite} aria-label={isFavorite ? "Aus Merkliste entfernen" : "Auf Merkliste setzen"} onClick={() => onToggleFavorite(vehicle.id)} data-testid="vehicle-detail-favorite-button"><Heart size={18} strokeWidth={1.5} fill={isFavorite ? "currentColor" : "none"} /></button>
          {images.length > 1 && <div className="detail-gallery-controls"><button type="button" onClick={() => showImage(activeImageIndex - 1)} aria-label="Vorheriges Bild" data-testid="vehicle-detail-image-prev-button"><ArrowLeft size={17} /></button><span data-testid="vehicle-detail-image-counter">{activeImageIndex + 1} / {images.length}</span><button type="button" onClick={() => showImage(activeImageIndex + 1)} aria-label="Nächstes Bild" data-testid="vehicle-detail-image-next-button"><ArrowRight size={17} /></button></div>}
        </div>
        {images.length > 1 && <>
          <div className="detail-thumbnails-head"><span className="mono-label">ALLE FOTOS · {images.length}</span><span className="toolbar-note">Klicken, wischen oder Pfeiltasten nutzen</span></div>
          <div className="detail-thumbnails" aria-label="Fahrzeugbilder" data-testid="vehicle-detail-thumbnails">{images.map((image, index) => <button type="button" className={index === activeImageIndex ? "is-active" : ""} key={`${image}-${index}`} onClick={() => showImage(index)} aria-label={`Bild ${index + 1} anzeigen`} aria-current={index === activeImageIndex} data-testid={`vehicle-detail-thumbnail-${index + 1}-button`}><img src={image} alt="" loading="lazy" /></button>)}</div>
        </>}
      </div>
      <div className="vehicle-detail-copy">
        <span className="section-kicker">{vehicle.brand} · EZ {vehicle.first_registration}</span>
        <h1 id="vehicle-detail-title">{vehicle.title}</h1>
        <p className="vehicle-detail-edition">{vehicle.edition} · {vehicle.gearbox}</p>
        <div className="vehicle-detail-pricing"><strong className="vehicle-detail-price" data-testid="vehicle-detail-price">{vehicle.price}</strong>{vehicle.monthly && <span className="vehicle-detail-monthly" data-testid="vehicle-detail-monthly">{vehicle.monthly}</span>}</div>
        <div className="vehicle-detail-highlights" data-testid="vehicle-detail-highlights"><span>{vehicle.mileage}</span><span>{vehicle.power}</span><span>{vehicle.fuel}</span><span>{vehicle.gearbox}</span><span>{vehicle.color}</span></div>
        <div className="vehicle-detail-actions"><button className="button button-primary" type="button" onClick={() => onPrepareInquiry(vehicle)} data-testid="vehicle-detail-inquiry-button"><Mail size={16} /> Anfrage vorbereiten</button><button className="button button-secondary" type="button" onClick={onBack} data-testid="vehicle-detail-inventory-button">Weitere Fahrzeuge</button></div>
        <AppointmentPicker vehicle={vehicle} onRequest={onPrepareInquiry} />
        <div className="vehicle-share"><ShareActions idPrefix="vehicle" label="FAHRZEUG TEILEN" title={`${vehicle.brand} ${vehicle.title} bei Moselcars`} text={`Schau dir diesen ${vehicle.brand} ${vehicle.title} (${vehicle.price}) bei Moselcars an:`} url={`${window.location.origin}/fahrzeuge/${encodeURIComponent(vehicle.id)}`} /></div>
        <div className="vehicle-detail-sheet" data-testid="vehicle-detail-sheet">
          {specGroups.map((group) => <div className="vehicle-detail-group" key={group.title}><h2>{group.title}</h2><dl>{group.rows.map(([label, value]) => <div key={label} data-testid={`vehicle-detail-spec-${specSlug(label)}`}><dt>{label}</dt><dd className={!value || value === NOT_SPECIFIED ? "is-muted" : ""}>{value || NOT_SPECIFIED}</dd></div>)}</dl></div>)}
        </div>
        <p className="vehicle-detail-note" data-testid="vehicle-detail-note">Alle Angaben stammen aus dem aktuellen Händlerbestand und werden stündlich aktualisiert. Ausstattungsdetails, Serviceheft und Besichtigungstermin klären wir gern persönlich.</p>
      </div>
    </div>
  </section>;
}
